import { Controller } from "@hotwired/stimulus"
import { toast } from "../utils/toast"

export default class extends Controller {
  static targets = ["input", "preview", "previewContainer", "uploadButton", "modal", "modalImage"]

  connect() {
    this.accumulatedPhotos = []
    this.currentPhotoIndex = 0
  }

  triggerPhotoLibrary() {
    const input = this.createPhotoLibraryInput()
    this.setupInputListener(input)
    input.click()
  }

  createPhotoLibraryInput() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    // No capture attribute - should prefer photo library
    input.multiple = true
    input.style.display = 'none'
    return input
  }

  setupInputListener(input) {
    document.body.appendChild(input)

    input.addEventListener('change', (e) => {
      this.handleFileSelection(e)
      document.body.removeChild(input)
    })
  }

  handleFileSelection(event) {
    const newFiles = Array.from(event.target.files)

    // Add new files to accumulated photos
    newFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        this.accumulatedPhotos.push(file)
      }
    })

    // Update preview display
    this.updatePhotoPreview()
  }

  openPhotoModal(event) {
    this.currentPhotoIndex = parseInt(event.params.index)
    this.showModal()
  }

  showModal() {
    if (this.hasModalTarget) {
      this.updateModalImage()
      this.modalTarget.classList.remove('hidden')
    }
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
    }
  }

  updateModalImage() {
    if (this.hasModalImageTarget && this.accumulatedPhotos[this.currentPhotoIndex]) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.modalImageTarget.src = e.target.result
      }
      reader.readAsDataURL(this.accumulatedPhotos[this.currentPhotoIndex])
    }
  }

  previousPhoto() {
    if (this.currentPhotoIndex > 0) {
      this.currentPhotoIndex--
      this.updateModalImage()
    }
  }

  nextPhoto() {
    if (this.currentPhotoIndex < this.accumulatedPhotos.length - 1) {
      this.currentPhotoIndex++
      this.updateModalImage()
    }
  }

  deleteCurrentPhoto() {
    this.accumulatedPhotos.splice(this.currentPhotoIndex, 1)

    if (this.accumulatedPhotos.length === 0) {
      this.closeModal()
      this.updatePhotoPreview()
    } else {
      // Adjust current index if we deleted the last photo
      if (this.currentPhotoIndex >= this.accumulatedPhotos.length) {
        this.currentPhotoIndex = this.accumulatedPhotos.length - 1
      }
      this.updateModalImage()
      this.updatePhotoPreview()
    }
  }

  removePhoto(event) {
    const index = parseInt(event.params.index)
    this.accumulatedPhotos.splice(index, 1)
    this.updatePhotoPreview()
  }

  updatePhotoPreview() {
    if (this.accumulatedPhotos.length > 0) {
      this.previewContainerTarget.innerHTML = ''

      this.accumulatedPhotos.forEach((file, index) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const previewItem = document.createElement('div')
          previewItem.className = 'relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity'
          previewItem.innerHTML = `
            <img src="${e.target.result}" class="w-full h-full object-cover" alt="Preview ${index + 1}">
            <div class="absolute top-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
              ${index + 1}
            </div>
          `
          previewItem.dataset.action = 'click->photo-upload#openPhotoModal'
          previewItem.dataset.photoUploadIndexParam = index
          this.previewContainerTarget.appendChild(previewItem)
        }
        reader.readAsDataURL(file)
      })

      this.previewTarget.classList.remove('hidden')
      if (this.hasUploadButtonTarget) {
        this.uploadButtonTarget.classList.remove('hidden')
      }
    } else {
      this.previewTarget.classList.add('hidden')
      if (this.hasUploadButtonTarget) {
        this.uploadButtonTarget.classList.add('hidden')
      }
    }
  }

  getAccumulatedPhotos() {
    return this.accumulatedPhotos
  }
}